import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { RouterPathEnum } from 'shared/enums/commonEnums';
import { LANGUAGE_CHANGED_EVENT } from 'shared/i18n/language';
import charactersStore from 'stores/charactersStore';
import languageStore from 'stores/languageStore';
import './App.css';
import 'sanitize.css';

const lazyNamed = <T extends Record<string, any>, K extends keyof T>(loader: () => Promise<T>, exportName: K) => lazy(() => loader().then((module) => ({ default: module[exportName] as React.ComponentType<any> })));
const RegisterPage = lazy(() => import('./pages/RegistrationPage/RegisterPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DoctorPersonalPage = lazy(() => import('./pages/DoctorPersonalPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const RootLayout = lazy(() => import('./pages/RootLayout'));
const CharacterPage = lazy(() => import('./pages/MainPage/CharacterPage'));
const CreateCharacterPage = lazy(() => import('./pages/CreateCharacterPage'));
const ScenePageMuscle = lazy(() => import('./pages/ScenePageMuscle'));
const FunctionalTrainingSources = lazy(() => import('./components/VRSessionReplay/FunctionalTrainingSources'));
const ScenePageMap = lazy(() => import('./pages/ScenePageMap'));
const ScenePageCognitive = lazy(() => import('./pages/ScenePageCognitive'));
const ScenePageAR = lazy(() => import('./pages/ScenePageAR/ScenePageAR'));
const ScenePageWear = lazy(() => import('./pages/ScenePageWear'));
const FirstPage = lazyNamed(() => import('./pages/FirstPage/FirstPage'), 'FirstPage');
const KeyLostPage = lazyNamed(() => import('./pages/KeyLostPage/KeyLostPage'), 'KeyLostPage');
const AboutExplanationPage = lazyNamed(() => import('./pages/AboutExplanationPage/AboutExplanationPage'), 'AboutExplanationPage');
const PersonalAccountFirstPage = lazyNamed(() => import('./pages/PersonalAccuntFirstPage/PersonalAccountFirstPage'), 'PersonalAccountFirstPage');
const WeekKBGUPage = lazyNamed(() => import('./pages/weekKBGU/WeekKBGUPage'), 'WeekKBGUPage');
const MetabolismPage = lazyNamed(() => import('./pages/MetabolismPage/MetabolismPage'), 'MetabolismPage');
const PsychologyPage = lazyNamed(() => import('./pages/PsychologyPage/PsychologyPage'), 'PsychologyPage');
const UserParametersPage = lazyNamed(() => import('./pages/UserParametersPage/UserParametersPage'), 'UserParametersPage');
const HeartRateDashboardPage = lazy(() => import('./pages/HeartRateDashboard'));
const BreathingAssessmentPage = lazy(() => import('./pages/BreathingAssessmentPage'));
const BreathingSessionDetailPage = lazyNamed(() => import('./pages/BreathingAssessmentPage'), 'BreathingSessionDetailPage');
const SpirometryTrainingDemoPage = lazyNamed(() => import('./pages/BreathingTrainingDemoPage'), 'SpirometryTrainingDemoPage');
const ImmersiveTrainingPage = lazy(() => import('./pages/ImmersiveTrainingPage/ImmersiveTrainingPage'));
const FineMotorPage = lazy(() => import('./pages/FineMotorPage'));
const ScenePageFineMotor = lazy(() => import('./pages/ScenePageFineMotor'));
const AlmanacPage = lazy(() => import('./pages/AlmanacPage'));
const RoutingPage = lazyNamed(() => import('./pages/RoutingPage/RoutingPage'), 'RoutingPage');
const GameRankingPage = lazyNamed(() => import('./pages/GameRankingPage/GameRankingPage'), 'GameRankingPage');
const RouteFallback = observer(() => (<div className="app-route-loader" role="status">
    {languageStore.t("generated.loading_106740de")}
  </div>));

const LanguageRouteBoundary = observer(({ element }: { element: React.ReactElement }) => {
    // cloneElement creates a fresh route element on language changes without changing
    // its type/key, so React re-renders the page but preserves component state.
    void languageStore.language;
    return React.cloneElement(element);
});

const withSuspense = (element: React.ReactElement) => (
    <Suspense fallback={<RouteFallback />}>
        <LanguageRouteBoundary element={element} />
    </Suspense>
);


const installViewportProfile = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => undefined;
    }

    const root = document.documentElement;
    const body = document.body;
    const mobileQuery = window.matchMedia('(max-width: 900px)');
    const portraitQuery = window.matchMedia('(orientation: portrait)');

    const update = () => {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
        const keyboardOpen = mobileQuery.matches &&
            viewportHeight < window.innerHeight * 0.78 &&
            Boolean(document.activeElement?.matches?.('input, textarea, select, [contenteditable="true"]'));

        root.style.setProperty('--app-vh', `${viewportHeight * 0.01}px`);
        root.style.setProperty('--app-viewport-height', `${viewportHeight}px`);
        root.style.setProperty('--app-viewport-width', `${viewportWidth}px`);
        body.classList.toggle('is-mobile', mobileQuery.matches);
        body.classList.toggle('is-portrait', portraitQuery.matches);
        body.classList.toggle('is-landscape', !portraitQuery.matches);
        body.classList.toggle('keyboard-open', keyboardOpen);
    };

    const onFocusChange = () => window.setTimeout(update, 40);
    update();

    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);
    window.visualViewport?.addEventListener('resize', update, { passive: true });
    window.visualViewport?.addEventListener('scroll', update, { passive: true });
    mobileQuery.addEventListener?.('change', update);
    portraitQuery.addEventListener?.('change', update);

    return () => {
        window.removeEventListener('resize', update);
        window.removeEventListener('orientationchange', update);
        document.removeEventListener('focusin', onFocusChange);
        document.removeEventListener('focusout', onFocusChange);
        window.visualViewport?.removeEventListener('resize', update);
        window.visualViewport?.removeEventListener('scroll', update);
        mobileQuery.removeEventListener?.('change', update);
        portraitQuery.removeEventListener?.('change', update);
        body.classList.remove('is-mobile', 'is-portrait', 'is-landscape', 'keyboard-open');
    };
};

const getDocumentTitle = () => languageStore.t('app.browserTitle');

const syncDocumentTitle = () => {
    if (typeof document !== 'undefined') {
        document.title = getDocumentTitle();
    }
};
const CreateCharacterWrapper: React.FC = () => {
    const navigate = useNavigate();
    return withSuspense(<CreateCharacterPage onBack={() => navigate(RouterPathEnum.CharacterChoice)} onCreated={async (updatedChars) => {
            charactersStore.characters = updatedChars;
            navigate(RouterPathEnum.CharacterChoice);
        }}/>);
};
const router = createBrowserRouter([
    { path: RouterPathEnum.FirstPage, element: withSuspense(<FirstPage />), errorElement: withSuspense(<ErrorPage />) },
    { path: RouterPathEnum.Auth, element: withSuspense(<LoginPage />) },
    { path: RouterPathEnum.RegistrationPage, element: withSuspense(<RegisterPage />) },
    { path: RouterPathEnum.KeyLost, element: withSuspense(<KeyLostPage />) },
    { path: RouterPathEnum.Explanation, element: withSuspense(<AboutExplanationPage />) },
    { path: RouterPathEnum.Routing, element: withSuspense(<RoutingPage />) },
    {
        path: RouterPathEnum.Profile,
        element: withSuspense(<RootLayout />),
        errorElement: withSuspense(<ErrorPage />),
        children: [
            { index: true, element: withSuspense(<PersonalAccountFirstPage />) },
            { path: RouterPathEnum.CharacterChoice, element: withSuspense(<CharacterPage />) },
            { path: RouterPathEnum.Almanac, element: withSuspense(<AlmanacPage />) },
            { path: RouterPathEnum.FineMotor, element: withSuspense(<FineMotorPage />) },
            { path: RouterPathEnum.SceneFineMotor, element: withSuspense(<ScenePageFineMotor />) },
            { path: RouterPathEnum.CreateCharacterPage, element: <CreateCharacterWrapper /> },
            { path: RouterPathEnum.KBGU, element: withSuspense(<WeekKBGUPage />) },
            { path: RouterPathEnum.SceneMap, element: withSuspense(<ScenePageMap />) },
            { path: RouterPathEnum.SceneCognitive, element: withSuspense(<ScenePageCognitive />) },
            { path: RouterPathEnum.SceneMuscle, element: withSuspense(<FunctionalTrainingSources><ScenePageMuscle /></FunctionalTrainingSources>) },
            { path: RouterPathEnum.ScenePage4, element: withSuspense(<ScenePageAR />) },
            { path: RouterPathEnum.SceneWear, element: withSuspense(<ScenePageWear />) },
            { path: RouterPathEnum.ScenePage6, element: withSuspense(<ImmersiveTrainingPage />) },
            { path: RouterPathEnum.ScenePage8, element: withSuspense(<SpirometryTrainingDemoPage />) },
            { path: RouterPathEnum.ScenePage7, element: <Navigate to={RouterPathEnum.SceneWear} replace /> },
            { path: RouterPathEnum.Metabolism, element: withSuspense(<MetabolismPage />) },
            { path: RouterPathEnum.Psychology, element: withSuspense(<PsychologyPage />) },
            { path: RouterPathEnum.UserParameters, element: withSuspense(<UserParametersPage />) },
            { path: RouterPathEnum.HeartRateDashboard, element: withSuspense(<HeartRateDashboardPage />) },
            { path: RouterPathEnum.BreathingAssessment, element: withSuspense(<BreathingAssessmentPage view="overview" />) },
            { path: RouterPathEnum.BreathingSpirometry, element: withSuspense(<BreathingAssessmentPage view="spirometry" />) },
            { path: RouterPathEnum.BreathingInterworld, element: withSuspense(<BreathingAssessmentPage view="interworld" />) },
            { path: RouterPathEnum.BreathingSpirometrySession, element: withSuspense(<BreathingSessionDetailPage source="spirometry" />) },
            { path: RouterPathEnum.BreathingInterworldSession, element: withSuspense(<BreathingSessionDetailPage source="interworld" />) },
            { path: RouterPathEnum.Dashboard, element: withSuspense(<DashboardPage />) },
            { path: RouterPathEnum.DoctorPage, element: withSuspense(<DoctorPersonalPage />) },
            { path: RouterPathEnum.Rankings, element: withSuspense(<GameRankingPage />) },
        ],
    },
]);
function App() {
    useEffect(() => {
        const uninstallViewportProfile = installViewportProfile();
        syncDocumentTitle();
        const onLanguageChanged = () => syncDocumentTitle();
        window.addEventListener(LANGUAGE_CHANGED_EVENT, onLanguageChanged);
        return () => {
            window.removeEventListener(LANGUAGE_CHANGED_EVENT, onLanguageChanged);
            uninstallViewportProfile();
        };
    }, []);
    return <RouterProvider router={router}/>;
}
export default App;
